import React, { Dispatch, SetStateAction } from 'react';

import { Flex } from '@reckon-web/flex';
import { XIcon } from '@reckon-web/icon';
import { IconButton } from '@reckon-web/button';
import { Divider } from '@reckon-web/divider';
import { LoadingDots } from '@reckon-web/loading';
import { makeId } from '@reckon-web/utils';

import { TaskStatusEnumValue } from '../work/types/task-status-enum';
import { useVisibleProperties } from '../common/visible-properties';
import { useVisibleFilters } from '../common/visible-filters';
import { useQueryTasks } from '../../hooks/use-query-tasks';
import { useExportToCsv } from '../../hooks/use-export-to-csv';
import { useOpenTaskDialog } from '../../hooks/use-open-task-dialog';
import { useQueryJob } from '../../hooks/use-query-job';
import { useTaskDrawer } from '../task/task-drawer-context';
import { DataControls } from '../work/data-controls';
import { KanbanOrList } from '../work/kanban-or-list';
import { SortType } from '../work/sort';
import {
  JobTasksFilter,
  emptyJobTasksFilter,
  equals,
  getActiveJobTasksFilter,
} from '../work/filter-helpers';
import { tasksToCsv } from '../work/export-helpers';
import { useTheme } from '../../../../../design-system/packages/theme/src';
import { useGetMyDetails } from '../../data/useGetMyDetails';

import { Filter } from './tasks/filter';
import { View } from './view';
import { Tasks } from './tasks/Tasks';
import { filterTasks } from './new-job';
import { ViewJobDialogDoCloseArgs } from './view-job-dialog';
import { JobStatusEnum } from './types/job-status-enum';

export const ViewJob: React.FC<{
  jobId: string;
  doClose: (args: ViewJobDialogDoCloseArgs) => void;
  setShouldPromptUnsaved: Dispatch<SetStateAction<boolean | undefined>>;
  testId?: string;
}> = ({ jobId, doClose, setShouldPromptUnsaved, testId = `view-job` }) => {
  const [visibleProperties] = useVisibleProperties();
  const [visibleFilters] = useVisibleFilters();
  const { data: currentUser } = useGetMyDetails();

  const openTaskDialog = useOpenTaskDialog();
  const { openCreate } = useTaskDrawer();
  const [ExportToCsvDialog, _exportToCsv] = useExportToCsv();

  const [kanbanOrList, setKanbanOrList] =
    React.useState<KanbanOrList>('kanban');
  const [sort, setSort] = React.useState<SortType>('Due Date (earliest first)');

  const [filter, _setFilter] =
    React.useState<JobTasksFilter>(emptyJobTasksFilter);
  const setFilter = (value: Partial<JobTasksFilter>) =>
    _setFilter((prev) => ({ ...prev, ...value }));
  const clearFilter = () => _setFilter(emptyJobTasksFilter);

  const activeFilter = React.useMemo(
    () => getActiveJobTasksFilter(filter, visibleFilters),
    [filter, visibleFilters]
  );
  const filtered = React.useMemo(
    () => !equals(activeFilter, emptyJobTasksFilter),
    [activeFilter]
  );

  const querySearchTasksArgs = React.useMemo(
    () => ({ myWork: false, jobs: [jobId] }),
    [jobId]
  );

  const tasksQueryResult = useQueryTasks({ variables: querySearchTasksArgs });

  const matchingTasks = React.useMemo(
    () =>
      filterTasks(
        tasksQueryResult.data,
        activeFilter,
        currentUser?.user?.cloudId || ''
      ),
    [tasksQueryResult, activeFilter, currentUser?.user]
  );

  const exportToCsv = React.useCallback(() => {
    if (matchingTasks) {
      _exportToCsv({
        exportWorkType: 'task',
        count: matchingTasks.length,
        csv: tasksToCsv(
          (() => {
            switch (kanbanOrList) {
              case 'kanban':
                return visibleProperties.taskBoard;
              case 'list':
                return visibleProperties.taskList;
            }
          })()
        )(matchingTasks),
      });
    }
  }, [visibleProperties, _exportToCsv, kanbanOrList, matchingTasks]);

  // this loads bits of the tasks, but then doesn't use them
  // it also doesn't pass the job to the View component, that component loads its own
  // I guess... cache... doesn't really matter. But, still
  // this could use a much simpler load job. The GetJobStatus would do the trick
  const {
    loading,
    error,
    data: job,
  } = useQueryJob({
    variables: { cloudId: jobId },
  });

  const openCreateTaskDrawer = React.useMemo(
    () =>
      job && job?.status.status !== JobStatusEnum.valuesEnum.Closed
        ? (opts?: { status?: TaskStatusEnumValue }) =>
            openCreate({ jobId: job.cloudId || '', ...opts })
        : undefined,
    [openCreate, job]
  );

  const theme = useTheme();

  if (loading)
    return (
      <Flex
        flexGrow={1}
        height="100%"
        justifyContent="center"
        alignItems="center"
      >
        <LoadingDots
          label="pending"
          size="medium"
          data-testid={makeId(testId, 'pending')}
        />
      </Flex>
    );
  if (error) return <div>Error!</div>; // TODO
  if (!job) return <div>Error!</div>; // TODO

  return (
    <React.Fragment>
      <Flex height="100%" data={{ testid: testId }}>
        <Flex width="448px" flexShrink={0} overflow="hidden">
          <View
            jobId={jobId}
            doClose={doClose}
            setShouldPromptUnsaved={setShouldPromptUnsaved}
            data-testid={makeId(testId, 'view')}
          />
        </Flex>
        <Divider vertical />
        <Flex
          flexGrow={1}
          overflow="auto"
          direction="column"
          background="muted"
        >
          <Flex gap="small" paddingX="xlarge" flexShrink={0}>
            <Flex paddingY="large">
              <Filter
                filter={filter}
                setFilter={setFilter}
                clearFilter={clearFilter}
                filtered={filtered}
                tasks={tasksQueryResult.data}
                testId={makeId(testId, 'filter')}
              />
            </Flex>
            <Flex flexGrow={1} />
            <Divider vertical />
            <Flex paddingLeft="small" paddingY="large" gap="medium">
              <DataControls
                jobsOrTasks="tasks"
                kanbanOrList={kanbanOrList}
                setKanbanOrList={setKanbanOrList}
                sort={sort}
                setSort={setSort}
                exportToCsv={exportToCsv}
                canExport={Boolean(matchingTasks?.length)}
                data-testid={makeId(testId, 'data-controls')}
              />
              <IconButton
                icon={XIcon}
                label="Exit dialog"
                tone="passive"
                weight="none"
                size="small"
                onClick={() => doClose({ bypassDirtyCheck: false })}
                css={{ marginRight: `-${theme.spacing.small}px` }}
                data-testid={makeId(testId, 'exit')}
              />
            </Flex>
          </Flex>
          <Divider />
          <Flex
            direction="column"
            flexGrow={1}
            paddingX="xlarge"
            paddingBottom="large"
            overflow="auto"
          >
            <Tasks
              kanbanOrList={kanbanOrList}
              queryResult={{
                data: matchingTasks,
                loading: tasksQueryResult.loading,
              }}
              filtered={filtered}
              clearFilter={clearFilter}
              sort={sort}
              onClickTask={openTaskDialog}
              onClickCreateTask={openCreateTaskDrawer}
              data-testid={makeId(testId, 'tasks')}
            />
          </Flex>
        </Flex>
      </Flex>
      <ExportToCsvDialog />
    </React.Fragment>
  );
};
